"use client"

import { useState, useEffect } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { getApiBaseUrl } from "../config/apiBase"

const useKeyUnknowns = (actionId, action) => {
  console.log("useKeyUnknowns hook initialized with actionId:", actionId)

  const [unknowns, setUnknowns] = useState([])
  const [suggestions, setSuggestions] = useState([])
  const [comments, setComments] = useState([])
  const [newSuggestion, setNewSuggestion] = useState([{ unknown: "", impact: "", resolution: "" }])
  const [newComment, setNewComment] = useState("")
  const [activeTab, setActiveTab] = useState("current")
  const [loading, setLoading] = useState(true)

  const { getAccessTokenSilently } = useAuth0()
  const apiUrl = getApiBaseUrl()
  console.log("API URL:", apiUrl)

  const fetchUnknownsData = async () => {
    console.log("fetchUnknownsData called with actionId:", actionId)

    if (!actionId) {
      console.log("No actionId provided, skipping fetch")
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      console.log("Token exists:", !!token)

      // Fetch the current unknowns
      const unknownsUrl = `${apiUrl}/api/actions/${actionId}/refinement/modules/keyUnknowns`
      console.log("Fetching unknowns from:", unknownsUrl)

      const unknownsResponse = await fetch(unknownsUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      console.log("Unknowns response status:", unknownsResponse.status)

      if (unknownsResponse.ok) {
        const unknownsData = await unknownsResponse.json()
        console.log("Unknowns data received:", unknownsData)
        console.log("Unknowns data type:", typeof unknownsData)
        console.log("Unknowns data is array:", Array.isArray(unknownsData))

        // Ensure we always set an array
        if (Array.isArray(unknownsData)) {
          setUnknowns(unknownsData)
        } else if (unknownsData && typeof unknownsData === "object") {
          // If it's an object, check if it has an array property
          if (Array.isArray(unknownsData.unknowns)) {
            setUnknowns(unknownsData.unknowns)
          } else if (Array.isArray(unknownsData.data)) {
            setUnknowns(unknownsData.data)
          } else {
            console.warn("Unknowns data is not in expected format:", unknownsData)
            setUnknowns([])
          }
        } else {
          console.warn("Unknowns data is not an array or object:", unknownsData)
          setUnknowns([])
        }
      } else {
        console.error("Unknowns response not ok:", unknownsResponse.status, unknownsResponse.statusText)
        setUnknowns([])
      }

      // Fetch suggestions
      const suggestionsUrl = `${apiUrl}/api/actions/${actionId}/refinement/modules/keyUnknowns/suggestions`
      console.log("Fetching suggestions from:", suggestionsUrl)

      const suggestionsResponse = await fetch(suggestionsUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      console.log("Suggestions response status:", suggestionsResponse.status)

      if (suggestionsResponse.ok) {
        const suggestionsData = await suggestionsResponse.json()
        console.log("Suggestions data received:", suggestionsData)

        // Ensure we always set an array
        if (Array.isArray(suggestionsData)) {
          setSuggestions(suggestionsData)
        } else if (suggestionsData && typeof suggestionsData === "object") {
          if (Array.isArray(suggestionsData.suggestions)) {
            setSuggestions(suggestionsData.suggestions)
          } else if (Array.isArray(suggestionsData.data)) {
            setSuggestions(suggestionsData.data)
          } else {
            setSuggestions([])
          }
        } else {
          setSuggestions([])
        }
      } else {
        console.error("Suggestions response not ok:", suggestionsResponse.status, suggestionsResponse.statusText)
        setSuggestions([])
      }

      // Fetch comments
      const commentsUrl = `${apiUrl}/api/actions/${actionId}/refinement/modules/keyUnknowns/comments`
      console.log("Fetching comments from:", commentsUrl)

      const commentsResponse = await fetch(commentsUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      console.log("Comments response status:", commentsResponse.status)

      if (commentsResponse.ok) {
        const commentsData = await commentsResponse.json()
        console.log("Comments data received:", commentsData)

        // Ensure we always set an array
        if (Array.isArray(commentsData)) {
          setComments(commentsData)
        } else if (commentsData && typeof commentsData === "object") {
          if (Array.isArray(commentsData.comments)) {
            setComments(commentsData.comments)
          } else if (Array.isArray(commentsData.data)) {
            setComments(commentsData.data)
          } else {
            setComments([])
          }
        } else {
          setComments([])
        }
      } else {
        console.error("Comments response not ok:", commentsResponse.status, commentsResponse.statusText)
        setComments([])
      }
    } catch (error) {
      console.error("Error fetching unknowns data:", error)
      // Ensure we set empty arrays on error
      setUnknowns([])
      setSuggestions([])
      setComments([])
    } finally {
      setLoading(false)
    }
  }

  const handleAddUnknown = () => {
    setNewSuggestion([...newSuggestion, { unknown: "", impact: "", resolution: "" }])
  }

  const handleRemoveUnknown = (index) => {
    if (newSuggestion.length > 1) {
      setNewSuggestion(newSuggestion.filter((_, i) => i !== index))
    }
  }

  const handleUnknownChange = (index, field, value) => {
    const updatedUnknowns = [...newSuggestion]
    updatedUnknowns[index][field] = value
    setNewSuggestion(updatedUnknowns)
  }

  const handleSubmitSuggestion = async () => {
    console.log("handleSubmitSuggestion called with:", newSuggestion)

    // Validate that at least one unknown has content
    if (!newSuggestion.some((item) => item.unknown.trim())) {
      console.log("No valid unknowns provided, skipping submit")
      return
    }

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const url = `${apiUrl}/api/actions/${actionId}/refinement/modules/keyUnknowns/suggestions`

      console.log("Submitting suggestion to:", url)
      console.log("Suggestion data:", { unknowns: newSuggestion })

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: JSON.stringify({ unknowns: newSuggestion }) }),
      })

      console.log("Submit suggestion response status:", response.status)

      if (response.ok) {
        setNewSuggestion([{ unknown: "", impact: "", resolution: "" }])
        await fetchUnknownsData()
      } else {
        console.error("Failed to submit suggestion:", response.status, response.statusText)
      }
    } catch (error) {
      console.error("Error submitting suggestion:", error)
    }
  }

  const handleSubmitComment = async () => {
    console.log("handleSubmitComment called with:", newComment)

    if (!newComment.trim()) {
      console.log("Empty comment, skipping submit")
      return
    }

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const url = `${apiUrl}/api/actions/${actionId}/refinement/modules/keyUnknowns/comments`

      console.log("Submitting comment to:", url)

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: newComment }),
      })

      console.log("Submit comment response status:", response.status)

      if (response.ok) {
        setNewComment("")
        await fetchUnknownsData()
      } else {
        console.error("Failed to submit comment:", response.status, response.statusText)
      }
    } catch (error) {
      console.error("Error submitting comment:", error)
    }
  }

  const handleVote = async (suggestionId, voteType) => {
    console.log("handleVote called with:", suggestionId, voteType)

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const url = `${apiUrl}/api/actions/${actionId}/refinement/suggestions/${suggestionId}/vote`

      console.log("Submitting vote to:", url)

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ voteType }),
      })

      console.log("Vote response status:", response.status)

      if (response.ok) {
        await fetchUnknownsData()
      } else {
        console.error("Failed to submit vote:", response.status, response.statusText)
      }
    } catch (error) {
      console.error("Error submitting vote:", error)
    }
  }

  useEffect(() => {
    console.log("useEffect triggered with actionId:", actionId)
    fetchUnknownsData()
  }, [actionId])

  console.log("useKeyUnknowns hook returning state:", {
    unknownsCount: Array.isArray(unknowns) ? unknowns.length : "NOT_ARRAY",
    unknownsType: typeof unknowns,
    suggestionsCount: Array.isArray(suggestions) ? suggestions.length : "NOT_ARRAY",
    commentsCount: Array.isArray(comments) ? comments.length : "NOT_ARRAY",
    loading,
  })

  return {
    // State
    unknowns: Array.isArray(unknowns) ? unknowns : [], // Ensure it's always an array
    suggestions: Array.isArray(suggestions) ? suggestions : [], // Ensure it's always an array
    comments: Array.isArray(comments) ? comments : [], // Ensure it's always an array
    newSuggestion,
    newComment,
    activeTab,
    loading,

    // Actions
    setActiveTab,
    setNewComment,
    handleAddUnknown,
    handleRemoveUnknown,
    handleUnknownChange,
    handleSubmitSuggestion,
    handleSubmitComment,
    handleVote,
    fetchUnknownsData,
  }
}

export default useKeyUnknowns
